/**
 * Vector rendering of a container (stage units = mm), for every product family.
 * Split into layers so printed ink sits between the body and the reflections:
 *   <ProductBack/> → canvas with ink → <ProductGloss/> (+ handle when in front).
 *
 * Lighting model (same for every product): a large soft window on the left,
 * a smaller fill on the right, a floor below. Transparent glass is rendered as
 * dense edges + clear centre + wall/base thickness; opaque materials (powder coat, steel,
 * opal, black glass) with cylindrical Lambert shading and glaze specular.
 */
import type { MaterialId } from '@shared/rfq';
import { ELLIPSE_RATIO } from './geometry';
import { CLOSURE_COLORS, MATERIAL_LOOK, buildSpec, radiusAt, rimRadius, shoulderY, type ContainerModel } from './model';
import { Grad, SliceStack, alphaAsGrey, colourOnly, glassStops, liquidStops, mix, opaqueStops, slices, slopeStops } from './shading';

const shoulderOf = (c: ContainerModel) => shoulderY(c);

interface Props {
  c: ContainerModel;
  material: MaterialId;
  uid: string;
  /** Needed for the handle: which side and whether it is in front. */
  viewAngleDeg?: number;
}

const E = ELLIPSE_RATIO;

/**
 * Horizontal stops for a lit cylinder: key light from the upper left,
 * soft fill from the right, dark terminator, bright rim on the far right.
 */
function cylinderStops(base: string, shadow: string, light = '#ffffff', strength = 1, contrast = 1): { o: number; c: string }[] {
  const out: { o: number; c: string }[] = [];
  for (let i = 0; i <= 16; i++) {
    const x = i / 16; // 0 left … 1 right
    const n = Math.asin(Math.max(-1, Math.min(1, 2 * x - 1))); // surface normal angle
    const key = Math.max(0, Math.cos(n + 0.62)); // window at upper-left
    const fill = 0.35 * Math.max(0, Math.cos(n - 1.0));
    const edge = Math.pow(Math.abs(2 * x - 1), 6);
    let v = 0.18 + 0.72 * key + fill; // 0.18 … ~1.1
    v = v * (1 - 0.55 * edge) + (x > 0.96 ? 0.25 * strength : 0);
    v = 0.85 - (0.85 - v) * contrast;
    const col = v >= 0.85 ? mix(base, light, Math.min(1, (v - 0.85) * 1.6 * strength)) : mix(shadow, base, Math.max(0, v / 0.85));
    out.push({ o: x, c: col });
  }
  return out;
}

// ------------------------------------------------------------------ paths
/** Closed outline of the body seen from the front, slightly from above. */
export function bodyPath(c: ContainerModel): string {
  const p = c.profile;
  const r0 = p[0][1];
  const H = c.heightMm;
  const rB = radiusAt(p, H);
  const right = p.map(([y, r]) => `L ${r.toFixed(2)} ${y.toFixed(2)}`).join(' ');
  const left = [...p]
    .reverse()
    .map(([y, r]) => `L ${(-r).toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
  return `M ${-r0} 0 A ${r0} ${E * r0} 0 0 1 ${r0} 0 ${right} L ${rB} ${H} A ${rB} ${E * rB} 0 0 1 ${-rB} ${H} ${left} Z`;
}

/** Inner volume between two heights (wall thickness removed). */
function innerPath(c: ContainerModel, y0: number, y1: number, wall: number): string {
  const pts: [number, number][] = [[y0, radiusAt(c.profile, y0) - wall]];
  for (const [y, r] of c.profile) if (y > y0 && y < y1) pts.push([y, r - wall]);
  pts.push([y1, radiusAt(c.profile, y1) - wall]);
  const rT = Math.max(0.5, pts[0][1]);
  const rB = Math.max(0.5, pts[pts.length - 1][1]);
  const right = pts.map(([y, r]) => `L ${Math.max(0.5, r).toFixed(2)} ${y.toFixed(2)}`).join(' ');
  const left = [...pts]
    .reverse()
    .map(([y, r]) => `L ${(-Math.max(0.5, r)).toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
  return `M ${-rT} ${y0} A ${rT} ${E * rT} 0 0 1 ${rT} ${y0} ${right} L ${rB} ${y1} A ${rB} ${E * rB} 0 0 1 ${-rB} ${y1} ${left} Z`;
}

/** Band following the outline: x from −a·r(y) to −b·r(y) (negative = left side). */
function bandPath(c: ContainerModel, a: number, b: number, y0: number, y1: number): string {
  const ys: number[] = [y0];
  for (const [y] of c.profile) if (y > y0 && y < y1) ys.push(y);
  ys.push(y1);
  const left = ys.map((y) => `${(-a * radiusAt(c.profile, y)).toFixed(2)} ${y.toFixed(2)}`);
  const right = [...ys].reverse().map((y) => `${(-b * radiusAt(c.profile, y)).toFixed(2)} ${y.toFixed(2)}`);
  return `M ${left.join(' L ')} L ${right.join(' L ')} Z`;
}

/** Inner wall line on one side (sign −1 left, 1 right). */
function wallLine(c: ContainerModel, sign: number, wall: number, y0: number, y1: number): string {
  const ys: number[] = [y0];
  for (const [y] of c.profile) if (y > y0 && y < y1) ys.push(y);
  ys.push(y1);
  return `M ${ys.map((y) => `${(sign * Math.max(0.5, radiusAt(c.profile, y) - wall)).toFixed(2)} ${y.toFixed(2)}`).join(' L ')}`;
}

// ------------------------------------------------------------------ handle
/** Handle geometry for a given view angle: x centre, facing (cos) and side sign. */
export function handlePose(c: ContainerModel, viewAngleDeg: number) {
  if (!c.handle || !c.noPrint?.length) return null;
  const r = c.diameterMm / 2;
  const theta = c.noPrint[0].uMm / r - (viewAngleDeg * Math.PI) / 180;
  return { x: r * Math.sin(theta), facing: Math.cos(theta), side: Math.sin(theta) >= 0 ? 1 : -1, reach: Math.abs(Math.sin(theta)) };
}

export function Handle({ c, material, uid, viewAngleDeg = 180 }: Props) {
  const pose = handlePose(c, viewAngleDeg);
  if (!pose || !c.handle) return null;
  const look = MATERIAL_LOOK[material];
  const r = c.diameterMm / 2;
  const H = c.heightMm;
  const w = c.handle.widthMm * Math.max(0.12, pose.reach);
  const x0 = pose.x * 0.97;
  const s = pose.side;
  const x1 = x0 + s * w;
  const top = H * 0.18;
  const bot = H * 0.76;
  const t = Math.max(5.5, H * 0.085);
  const yT = top + E * r * pose.facing;
  const yB = bot + E * r * pose.facing;
  const mid = (yT + yB) / 2;
  const d = [
    `M ${x0} ${yT}`,
    `C ${x1 - s * 2} ${yT - 3}, ${x1 + s * 3} ${yT + 4}, ${x1 + s * 2} ${mid}`,
    `C ${x1 + s * 3} ${yB - 4}, ${x1 - s * 2} ${yB + 3}, ${x0} ${yB}`,
    `L ${x0} ${yB - t}`,
    `C ${x1 - s * t * 0.75} ${yB - t + 1}, ${x1 - s * t * 0.55} ${yB - t - 4}, ${x1 - s * t * 0.6} ${mid}`,
    `C ${x1 - s * t * 0.55} ${yT + t + 4}, ${x1 - s * t * 0.75} ${yT + t - 1}, ${x0} ${yT + t}`,
    'Z',
  ].join(' ');
  const gid = `${uid}-handle`;
  return (
    <g opacity={look.opacity < 0.9 ? 0.85 : 1}>
      <defs>
        <linearGradient id={gid} x1={s > 0 ? 0 : 1} x2={s > 0 ? 1 : 0} y1="0" y2="0">
          <stop offset="0" stopColor={mix(look.edge, look.body, 0.4)} />
          <stop offset="0.45" stopColor={look.body} />
          <stop offset="0.7" stopColor={mix(look.body, '#ffffff', look.base === 'dark' ? 0.25 : 0.5)} />
          <stop offset="1" stopColor={look.edge} />
        </linearGradient>
      </defs>
      <path d={d} fill={`url(#${gid})`} stroke={look.edge} strokeOpacity="0.6" strokeWidth="0.5" />
    </g>
  );
}

// ------------------------------------------------------------------ closures
function Closure({ c, uid }: { c: ContainerModel; uid: string }) {
  const cl = c.closure;
  if (!cl) return null;
  const r0 = rimRadius(c);
  const [dark, light, deep] = CLOSURE_COLORS[cl.finish];
  const metal = cl.finish === 'gold' || cl.finish === 'silver';
  const gid = `${uid}-cl`;
  const topId = `${uid}-cltop`;
  const stops = cylinderStops(dark, deep, metal ? '#ffffff' : light, metal ? 1.4 : 0.7);
  const defs = (
    <defs>
      <linearGradient id={gid} x1="0" x2="1" y1="0" y2="0">
        {stops.map((st) => (
          <stop key={st.o} offset={st.o} stopColor={st.c} />
        ))}
        {metal && <stop offset="0.3" stopColor={light} />}
      </linearGradient>
      <radialGradient id={topId} cx="0.35" cy="0.3" r="0.8">
        <stop offset="0" stopColor={mix(light, '#ffffff', metal ? 0.5 : 0.2)} />
        <stop offset="0.6" stopColor={light} />
        <stop offset="1" stopColor={dark} />
      </radialGradient>
    </defs>
  );
  const cyl = (r: number, yTop: number, yBot: number, key?: string) => (
    <g key={key}>
      <path d={`M ${-r} ${yTop} A ${r} ${E * r} 0 0 1 ${r} ${yTop} L ${r} ${yBot} A ${r} ${E * r} 0 0 1 ${-r} ${yBot} Z`} fill={`url(#${gid})`} />
      <ellipse cx="0" cy={yTop} rx={r} ry={E * r} fill={`url(#${topId})`} />
      <ellipse cx="0" cy={yTop} rx={r} ry={E * r} fill="none" stroke={mix(light, '#ffffff', 0.4)} strokeOpacity="0.6" strokeWidth="0.35" />
      <path d={`M ${-r} ${yBot} A ${r} ${E * r} 0 0 0 ${r} ${yBot}`} fill="none" stroke={deep} strokeOpacity="0.5" strokeWidth="0.5" />
    </g>
  );
  const grain = (r: number, yTop: number, yBot: number) =>
    Array.from({ length: 7 }, (_, i) => {
      const y = yTop + 2 + ((yBot - yTop - 3) * i) / 6;
      return <path key={i} d={`M ${-r + 0.5} ${y} Q ${-r * 0.3} ${y + 0.8 * Math.sin(i)} ${r * 0.2} ${y - 0.4} T ${r - 0.5} ${y + 0.3}`} fill="none" stroke={deep} strokeOpacity="0.22" strokeWidth="0.3" />;
    });

  switch (cl.kind) {
    case 'lid': {
      const rl = r0 + 1.4;
      const yT = -cl.heightMm + 3;
      return (
        <g>
          {defs}
          {cyl(rl, yT, 5)}
          {cl.finish === 'wood' && grain(rl, yT, 5)}
          <ellipse cx="0" cy={yT} rx={rl - 3} ry={E * (rl - 3)} fill="none" stroke={deep} strokeWidth="0.35" opacity="0.45" />
        </g>
      );
    }
    case 'cap': {
      const rc = r0 + 0.9;
      const yT = -cl.heightMm + 2;
      const ribs = Math.round(rc * 1.4);
      return (
        <g>
          {defs}
          {cyl(rc, yT, 3)}
          {Array.from({ length: ribs }, (_, i) => {
            const a = -Math.PI / 2 + (Math.PI * (i + 0.5)) / ribs;
            const x = rc * Math.sin(a);
            return <line key={i} x1={x} x2={x} y1={yT + E * rc * Math.cos(a) + 1.2} y2={2.4 + E * rc * Math.cos(a)} stroke={deep} strokeOpacity={0.18 + 0.25 * Math.abs(Math.sin(a))} strokeWidth="0.3" />;
          })}
        </g>
      );
    }
    case 'cork': {
      const rt = r0 + 1.8;
      const top = -cl.heightMm + 4;
      return (
        <g>
          {defs}
          <path d={`M ${-rt} ${top} A ${rt} ${E * rt} 0 0 1 ${rt} ${top} L ${r0 - 0.3} 4 A ${r0} ${E * r0} 0 0 1 ${-(r0 - 0.3)} 4 Z`} fill={`url(#${gid})`} />
          <ellipse cx="0" cy={top} rx={rt} ry={E * rt} fill={`url(#${topId})`} />
          {Array.from({ length: 28 }, (_, i) => (
            <ellipse key={i} cx={-rt + 1 + ((i * 7.31) % (2 * rt - 2))} cy={top + 1.5 + ((i * 3.71) % (cl.heightMm - 5))} rx="0.45" ry="0.3" fill={deep} opacity={0.25 + (i % 3) * 0.12} />
          ))}
        </g>
      );
    }
    case 'dropper': {
      const collarH = 11;
      const rb = r0 * 0.78;
      const bulbTop = -cl.heightMm + 2;
      const bid = `${uid}-bulb`;
      return (
        <g>
          {defs}
          <defs>
            <linearGradient id={bid} x1="0" x2="1" y1="0" y2="0">
              {cylinderStops('#2a2a2a', '#0a0a0a', '#9a9a9a', 0.8).map((st) => (
                <stop key={st.o} offset={st.o} stopColor={st.c} />
              ))}
            </linearGradient>
          </defs>
          <path
            d={`M ${-rb} ${-collarH + 1} L ${-rb} ${bulbTop + rb} Q ${-rb} ${bulbTop} 0 ${bulbTop} Q ${rb} ${bulbTop} ${rb} ${bulbTop + rb} L ${rb} ${-collarH + 1} Z`}
            fill={`url(#${bid})`}
          />
          {cyl(r0 + 1, -collarH, 3)}
        </g>
      );
    }
    case 'pump': {
      const collarH = 10;
      const stemR = 2.2;
      const headTop = -cl.heightMm + 2;
      return (
        <g>
          {defs}
          <rect x={-stemR} y={headTop + 8} width={stemR * 2} height={cl.heightMm - collarH - 8} fill={`url(#${gid})`} />
          <path
            d={`M ${-r0 * 0.75} ${headTop + 9} L ${-r0 * 0.75} ${headTop + 2} Q ${-r0 * 0.75} ${headTop} ${-r0 * 0.5} ${headTop} L ${r0 * 0.9} ${headTop} L ${r0 * 1.6} ${headTop + 1.5} L ${r0 * 1.6} ${headTop + 4} L ${r0 * 0.75} ${headTop + 5} L ${r0 * 0.75} ${headTop + 9} Z`}
            fill={`url(#${gid})`}
            stroke={deep}
            strokeOpacity="0.4"
            strokeWidth="0.3"
          />
          {cyl(r0 + 1, -collarH, 3)}
        </g>
      );
    }
    case 'thermo': {
      // screw-on steel / bamboo cup: smooth cylinder, two grip grooves, slightly domed top
      const rc = r0 + 0.7;
      const yT = -cl.heightMm + 3;
      const grooves = [0.3, 0.38, 0.7];
      return (
        <g>
          {defs}
          {cyl(rc, yT, 3)}
          {(cl.finish === 'bamboo' || cl.finish === 'wood') && grain(rc, yT, 3)}
          {grooves.map((g) => {
            const y = yT + (3 - yT) * g;
            return (
              <g key={g}>
                <path d={`M ${-rc} ${y} A ${rc} ${E * rc} 0 0 0 ${rc} ${y}`} fill="none" stroke={deep} strokeOpacity="0.45" strokeWidth="0.45" />
                <path d={`M ${-rc} ${y + 0.6} A ${rc} ${E * rc} 0 0 0 ${rc} ${y + 0.6}`} fill="none" stroke="#fff" strokeOpacity={metal ? 0.5 : 0.25} strokeWidth="0.3" />
              </g>
            );
          })}
          <ellipse cx="0" cy={yT} rx={rc * 0.82} ry={E * rc * 0.82} fill="#fff" opacity={metal ? 0.18 : 0.08} />
        </g>
      );
    }
    case 'sport': {
      // sport cap: wide collar, flip spout and a carry loop
      const rc = r0 + 0.8;
      const collarT = -14;
      const rs = Math.max(5, r0 * 0.36);
      const spoutT = -cl.heightMm + 3;
      const loopR = rc * 0.55;
      return (
        <g>
          {defs}
          <path
            d={`M ${rc * 0.25} ${collarT + 3} C ${rc * 0.3} ${collarT - loopR * 1.3}, ${rc * 1.25} ${collarT - loopR * 1.3}, ${rc * 0.95} ${collarT + 4}`}
            fill="none"
            stroke={deep}
            strokeWidth="3.6"
            strokeLinecap="round"
          />
          <path
            d={`M ${rc * 0.25} ${collarT + 3} C ${rc * 0.3} ${collarT - loopR * 1.3}, ${rc * 1.25} ${collarT - loopR * 1.3}, ${rc * 0.95} ${collarT + 4}`}
            fill="none"
            stroke={light}
            strokeOpacity="0.35"
            strokeWidth="1"
            strokeLinecap="round"
          />
          {cyl(rs, spoutT, collarT + 2)}
          {cyl(rc, collarT, 3)}
          <path d={`M ${-rc} ${collarT + 9} A ${rc} ${E * rc} 0 0 0 ${rc} ${collarT + 9}`} fill="none" stroke={deep} strokeOpacity="0.5" strokeWidth="0.5" />
        </g>
      );
    }
    case 'swing': {
      // swing-top: porcelain stopper on an orange rubber seal, wire bail to the neck bead
      const rs = r0 * 0.92;
      const top = -cl.heightMm;
      const sid = `${uid}-porcelain`;
      const wire = '#9aa1a5';
      const hole = top * 0.45;
      const bead = 11.3;
      const xb = r0 + 2.1;
      return (
        <g>
          <defs>
            <linearGradient id={sid} x1="0" x2="1" y1="0" y2="0">
              {cylinderStops('#f3f1ec', '#b9b4aa', '#ffffff', 0.9, 0.7).map((st) => (
                <stop key={st.o} offset={st.o} stopColor={st.c} />
              ))}
            </linearGradient>
          </defs>
          {/* back wire */}
          <path d={`M ${-rs * 0.97} ${hole} L ${-xb} ${bead}`} stroke={mix(wire, '#000000', 0.35)} strokeWidth="0.7" fill="none" />
          <path d={`M ${-r0 - 0.4} -2.4 L ${-r0 - 0.4} 0.6 A ${r0 + 0.4} ${E * (r0 + 0.4)} 0 0 0 ${r0 + 0.4} 0.6 L ${r0 + 0.4} -2.4 Z`} fill="#d8652c" />
          <path d={`M ${-r0 - 0.4} -2.4 A ${r0 + 0.4} ${E * (r0 + 0.4)} 0 0 0 ${r0 + 0.4} -2.4`} fill="none" stroke="#f09a60" strokeWidth="0.35" />
          <path
            d={`M ${-rs} -2.2 L ${-rs} ${top + 6} Q ${-rs} ${top} 0 ${top} Q ${rs} ${top} ${rs} ${top + 6} L ${rs} -2.2 A ${rs} ${E * rs} 0 0 1 ${-rs} -2.2 Z`}
            fill={`url(#${sid})`}
          />
          <ellipse cx={-rs * 0.35} cy={top + 3} rx={rs * 0.35} ry={1.6} fill="#fff" opacity="0.7" />
          {/* wire bail and lever */}
          <circle cx={rs * 0.97} cy={hole} r="0.8" fill={wire} />
          <path d={`M ${rs * 0.97} ${hole} L ${xb} ${bead}`} stroke={wire} strokeWidth="0.75" fill="none" />
          <path d={`M ${xb} ${bead} C ${xb + 2.4} ${bead + 6}, ${xb + 2.6} ${bead + 16}, ${xb + 0.4} ${bead + 22}`} stroke={wire} strokeWidth="1" fill="none" strokeLinecap="round" />
          <path d={`M ${xb - 0.2} ${bead} C ${xb + 2.2} ${bead + 6}, ${xb + 2.4} ${bead + 16}, ${xb + 0.2} ${bead + 22}`} stroke="#ffffff" strokeOpacity="0.6" strokeWidth="0.3" fill="none" />
          <path d={`M ${-xb} ${bead} A ${xb} ${E * xb} 0 0 0 ${xb} ${bead}`} stroke={wire} strokeWidth="0.7" fill="none" />
        </g>
      );
    }
    case 'tcork': {
      // bar-top stopper: wooden head over a natural cork shank
      const rh = r0 + 2.2;
      const headB = -4.5;
      const headT = -cl.heightMm + 2;
      const rk = r0 - 1.2;
      return (
        <g>
          {defs}
          <path d={`M ${-rk} ${headB} L ${-rk} 0.5 A ${rk} ${E * rk} 0 0 0 ${rk} 0.5 L ${rk} ${headB} Z`} fill={CLOSURE_COLORS.cork[1]} />
          {Array.from({ length: 10 }, (_, i) => (
            <ellipse key={i} cx={-rk + 1 + ((i * 5.3) % (2 * rk - 2))} cy={headB + 0.8 + ((i * 1.7) % 4)} rx="0.4" ry="0.25" fill={CLOSURE_COLORS.cork[2]} opacity="0.5" />
          ))}
          {cyl(rh, headT, headB)}
          {grain(rh, headT, headB)}
          <path d={`M ${-rh} ${headT + 1.2} A ${rh} ${E * rh} 0 0 0 ${rh} ${headT + 1.2}`} fill="none" stroke={light} strokeOpacity="0.5" strokeWidth="0.4" />
        </g>
      );
    }
    case 'reeds': {
      const n = 11;
      return (
        <g>
          {Array.from({ length: n }, (_, i) => {
            const t = i / (n - 1) - 0.5;
            const xb = t * r0 * 1.2;
            const xt = t * r0 * 7 + Math.sin(i * 1.7) * 2;
            const yt = -cl.heightMm + 4 + Math.abs(t) * 10;
            return (
              <g key={i}>
                <line x1={xb} y1={3} x2={xt} y2={yt} stroke={i % 3 === 0 ? dark : light} strokeWidth="1.5" strokeLinecap="round" />
                <line x1={xb - 0.35} y1={3} x2={xt - 0.35} y2={yt} stroke="#fff" strokeOpacity="0.35" strokeWidth="0.35" strokeLinecap="round" />
              </g>
            );
          })}
        </g>
      );
    }
  }
}

// ------------------------------------------------------------------ body
/** Glass edge density (wall / local radius), quantised so slices can share gradients. */
const edgeKey = (wall: number, r: number) => Math.round(Math.max(0.03, Math.min(0.22, (wall / Math.max(1, r)) * 1.15)) * 250);

export function ProductBack({ c, material, uid, viewAngleDeg = 180 }: Props) {
  const look = MATERIAL_LOOK[material];
  const spec = buildSpec(c);
  const r = c.diameterMm / 2;
  const H = c.heightMm;
  const r0 = rimRadius(c);
  const wall = spec.wallMm;
  const baseT = spec.baseMm;
  const id = (s: string) => `${uid}-${s}`;
  const open = !c.closure || c.closure.kind === 'reeds';
  const pose = handlePose(c, viewAngleDeg);
  const cont = c.contents;
  const contTop = cont.topMm;
  const tint = cont.tint ?? '#f3ede2';
  /** Transparent glass gets the photographic treatment. */
  const clear = look.opacity < 0.9;
  const tinted = look.base === 'tinted';
  const rB = radiusAt(c.profile, H);
  const punt = spec.puntMm;
  const innerR = r0 - wall;
  const visibleFill = cont.kind !== 'none' && look.contentVisible > 0;
  const deep = tinted ? mix(look.edge, '#000000', 0.35) : mix(look.edge, '#0d1714', 0.62);
  const extend = E * Math.max(r, r0) + 1.5;

  return (
    <g>
      <defs>
        <clipPath id={id('clip')}>
          <path d={bodyPath(c)} />
        </clipPath>
        <Grad id={id('fill')} stops={opaqueStops({ ...look, body: tint, edge: mix(tint, '#5a4a36', 0.35), base: 'white', coated: true })} />
        <Grad id={id('liquid')} stops={liquidStops(tint)} />
        <linearGradient id={id('liquidDepth')} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="0.25" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.22" />
        </linearGradient>
        <radialGradient id={id('shadow')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#000" stopOpacity="0.36" />
          <stop offset="0.5" stopColor="#000" stopOpacity="0.13" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={id('caustic')} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={tinted ? mix(look.body, '#ffd9a0', 0.4) : '#ffffff'} stopOpacity={tinted ? 0.55 : 0.75} />
          <stop offset="1" stopColor={tinted ? look.body : '#ffffff'} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={id('inside')} cx="0.5" cy="0.15" r="0.9">
          <stop offset="0" stopColor={mix(look.inner, '#ffffff', 0.25)} />
          <stop offset="1" stopColor={mix(look.inner, '#000000', look.base === 'dark' ? 0.4 : 0.2)} />
        </radialGradient>
        {clear ? (
          <>
            {[...new Set(slices(c).map((sl) => edgeKey(wall, sl.r)))].map((k) => (
              <g key={k}>
                <Grad id={id(`g${k}`)} stops={colourOnly(glassStops(look, k / 250))} />
                <Grad id={id(`m${k}`)} stops={alphaAsGrey(glassStops(look, k / 250))} />
              </g>
            ))}
            <mask id={id('glassMask')} maskUnits="userSpaceOnUse" x={-r - 10} y={-extend - 10} width={2 * r + 20} height={H + 2 * extend + 20}>
              <SliceStack c={c} clipId={id('clip')} extendBottom={extend} fillKey={`${uid}m${wall}`} fill={(s) => `url(#${id(`m${edgeKey(wall, s.r)}`)})`} />
            </mask>
            <Grad id={id('slopeDark')} user={{ y1: 0, y2: H }} stops={slopeStops(c, 'light', tinted ? 0.4 : look.frosted ? 0.18 : 0.38, deep)} />
            <Grad id={id('heel')} user={{ y1: 0, y2: H }} stops={slopeStops(c, 'shade', tinted ? 0.4 : 0.3, deep)} />
            <Grad
              id={id('base')}
              stops={
                look.frosted
                  ? [
                      { o: 0, c: mix(look.edge, '#55625f', 0.35), a: 0.85 },
                      { o: 0.1, c: '#f4f6f5', a: 0.7 },
                      { o: 0.5, c: '#ffffff', a: 0.62 },
                      { o: 0.9, c: '#eef1f0', a: 0.7 },
                      { o: 1, c: mix(look.edge, '#55625f', 0.35), a: 0.85 },
                    ]
                  : [
                      { o: 0, c: deep, a: 0.85 },
                      { o: 0.06, c: deep, a: tinted ? 0.75 : 0.45 },
                      { o: 0.16, c: look.body, a: tinted ? 0.7 : 0.22 },
                      { o: 0.5, c: look.body, a: tinted ? 0.62 : 0.14 },
                      { o: 0.84, c: look.body, a: tinted ? 0.7 : 0.22 },
                      { o: 0.94, c: deep, a: tinted ? 0.75 : 0.45 },
                      { o: 1, c: deep, a: 0.85 },
                    ]
              }
            />
            <radialGradient id={id('punt')} cx="0.45" cy="0.2" r="0.8">
              <stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
              <stop offset="0.6" stopColor={look.body} stopOpacity={tinted ? 0.4 : 0.12} />
              <stop offset="1" stopColor={deep} stopOpacity="0.35" />
            </radialGradient>
          </>
        ) : (
          <>
            <Grad id={id('body')} stops={opaqueStops(look)} />
            <Grad id={id('slopeLight')} user={{ y1: 0, y2: H }} stops={slopeStops(c, 'light', look.metal ? 0.45 : look.base === 'dark' ? 0.22 : 0.3, '#ffffff')} />
            <Grad id={id('heel')} user={{ y1: 0, y2: H }} stops={slopeStops(c, 'shade', look.base === 'dark' ? 0.6 : 0.4, '#000000')} />
            <linearGradient id={id('ao')} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#000" stopOpacity="0" />
              <stop offset="0.88" stopColor="#000" stopOpacity="0" />
              <stop offset="1" stopColor="#000" stopOpacity={look.base === 'dark' ? 0.3 : 0.14} />
            </linearGradient>
          </>
        )}
        {look.frosted && (
          <filter id={id('frost')} x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="1.1" />
          </filter>
        )}
        <filter id={id('blur')} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>

      {/* floor: soft contact shadow; glass also focuses light onto the floor */}
      <ellipse cx={r * 0.18} cy={H + E * r * 0.7} rx={r * 1.45} ry={E * r * 2.2} fill={`url(#${id('shadow')})`} />
      <ellipse cx={0} cy={H + E * rB * 0.15} rx={rB * 1.02} ry={E * rB * 1.05} fill="#000" opacity={look.base === 'dark' ? 0.32 : 0.2} filter={`url(#${id('blur')})`} />
      {clear && <ellipse cx={r * 0.55} cy={H + E * r * 1.55} rx={r * 0.62} ry={E * r * 0.75} fill={`url(#${id('caustic')})`} />}

      {pose && pose.facing < 0 && <Handle c={c} material={material} uid={uid} viewAngleDeg={viewAngleDeg} />}

      {/* contents seen through the body */}
      {visibleFill && (
        <g opacity={look.contentVisible} filter={look.frosted ? `url(#${id('frost')})` : undefined}>
          <path d={innerPath(c, contTop, H - baseT, wall)} fill={cont.kind === 'liquid' ? `url(#${id('liquid')})` : `url(#${id('fill')})`} />
          {cont.kind === 'liquid' && <path d={innerPath(c, contTop, H - baseT, wall)} fill={`url(#${id('liquidDepth')})`} />}
          <ellipse
            cx="0"
            cy={contTop}
            rx={radiusAt(c.profile, contTop) - wall}
            ry={E * (radiusAt(c.profile, contTop) - wall)}
            fill={cont.kind === 'liquid' ? mix(tint, '#ffffff', 0.35) : mix(tint, '#ffffff', 0.25)}
            opacity={cont.kind === 'liquid' ? 0.8 : 1}
          />
          {cont.kind === 'liquid' && (
            <path
              d={`M ${-(radiusAt(c.profile, contTop) - wall)} ${contTop} A ${radiusAt(c.profile, contTop) - wall} ${E * (radiusAt(c.profile, contTop) - wall)} 0 0 0 ${radiusAt(c.profile, contTop) - wall} ${contTop}`}
              fill="none"
              stroke="#fff"
              strokeOpacity="0.7"
              strokeWidth="0.4"
            />
          )}
          {cont.kind === 'wax' && open && <line x1="0" y1={contTop} x2="0" y2={contTop - 7} stroke="#2b2622" strokeWidth="1.1" strokeLinecap="round" />}
        </g>
      )}

      {/* dropper pipette inside a transparent bottle */}
      {clear && c.closure?.kind === 'dropper' && (
        <g opacity="0.55">
          <rect x={-1.6} y={2} width={3.2} height={H * 0.72} rx={1.6} fill="none" stroke={look.edge} strokeWidth="0.5" />
          <rect x={-0.7} y={contTop} width={1.4} height={H * 0.72 - contTop + 1} fill={mix(tint, '#6b4d1f', 0.3)} opacity="0.6" />
        </g>
      )}

      {/* body */}
      {clear ? (
        <g>
          {/* back wall: inner surface seen through the glass */}
          <path d={wallLine(c, -1, wall, Math.max(2, shoulderOf(c)), H - baseT)} fill="none" stroke={deep} strokeOpacity={tinted ? 0.35 : 0.22} strokeWidth="0.5" />
          <path d={wallLine(c, 1, wall, Math.max(2, shoulderOf(c)), H - baseT)} fill="none" stroke={deep} strokeOpacity={tinted ? 0.35 : 0.22} strokeWidth="0.5" />
          <g mask={`url(#${id('glassMask')})`}>
            <SliceStack c={c} clipId={id('clip')} extendBottom={extend} fillKey={`${uid}g${wall}`} fill={(s) => `url(#${id(`g${edgeKey(wall, s.r)}`)})`} />
          </g>
          <path d={bodyPath(c)} fill={`url(#${id('slopeDark')})`} />
          <path d={bodyPath(c)} fill={`url(#${id('heel')})`} />
          {/* heavy base: a thick lens of glass with a lit inner floor */}
          <g clipPath={`url(#${id('clip')})`}>
            <rect x={-rB} y={H - baseT} width={rB * 2} height={baseT + E * rB + 1} fill={`url(#${id('base')})`} />
          </g>
          <path d={`M ${-rB + wall} ${H - baseT} A ${rB - wall} ${E * (rB - wall)} 0 0 0 ${rB - wall} ${H - baseT}`} fill="none" stroke="#fff" strokeOpacity="0.85" strokeWidth="0.6" />
          <path d={`M ${-rB + wall} ${H - baseT + 0.9} A ${rB - wall} ${E * (rB - wall)} 0 0 0 ${rB - wall} ${H - baseT + 0.9}`} fill="none" stroke={deep} strokeOpacity="0.4" strokeWidth="0.5" />
          <path d={`M ${-rB * 0.9} ${H + E * rB * 0.35} A ${rB * 0.9} ${E * rB * 0.9} 0 0 0 ${rB * 0.9} ${H + E * rB * 0.35}`} fill="none" stroke="#fff" strokeOpacity="0.5" strokeWidth="0.5" />
          {punt > 0 && (
            <path
              d={`M ${-rB * 0.62} ${H + E * rB * 0.55} C ${-rB * 0.58} ${H - punt * 0.7}, ${-rB * 0.3} ${H - punt} 0 ${H - punt} C ${rB * 0.3} ${H - punt}, ${rB * 0.58} ${H - punt * 0.7} ${rB * 0.62} ${H + E * rB * 0.55}`}
              fill={`url(#${id('punt')})`}
              stroke="#fff"
              strokeOpacity="0.6"
              strokeWidth="0.45"
            />
          )}
          <path d={bodyPath(c)} fill="none" stroke={deep} strokeOpacity={tinted ? 0.6 : 0.4} strokeWidth="0.3" />
        </g>
      ) : (
        <g>
          <SliceStack c={c} clipId={id('clip')} extendBottom={extend} fillKey={`${uid}body`} fill={() => `url(#${id('body')})`} />
          <path d={bodyPath(c)} fill={`url(#${id('slopeLight')})`} />
          <path d={bodyPath(c)} fill={`url(#${id('heel')})`} />
          <path d={bodyPath(c)} fill={`url(#${id('ao')})`} />
          <path d={bodyPath(c)} fill="none" stroke={mix(look.edge, '#000000', look.base === 'dark' ? 0.5 : 0.2)} strokeOpacity="0.55" strokeWidth="0.35" />
        </g>
      )}

      {/* rim / opening */}
      {open && (
        <g>
          <ellipse cx="0" cy="0" rx={r0} ry={E * r0} fill={clear ? mix(look.body, look.edge, 0.5) : mix(look.body, '#ffffff', look.base === 'dark' ? 0.12 : 0.35)} />
          <ellipse cx="0" cy="0" rx={innerR} ry={E * innerR} fill={`url(#${id('inside')})`} />
          {cont.kind === 'wax' && look.contentVisible === 0 && (
            <>
              <ellipse cx="0" cy={E * innerR * 0.35} rx={innerR * 0.92} ry={E * innerR * 0.6} fill="#efe8dc" opacity="0.92" />
              <line x1="0" y1="1" x2="0" y2="-5" stroke="#2b2622" strokeWidth="1.1" strokeLinecap="round" />
            </>
          )}
          {/* lit front lip */}
          <path d={`M ${-r0} 0 A ${r0} ${E * r0} 0 0 0 ${r0} 0`} fill="none" stroke="#fff" strokeOpacity={look.base === 'dark' ? 0.35 : 0.8} strokeWidth="0.55" />
          <ellipse cx="0" cy="0" rx={r0} ry={E * r0} fill="none" stroke={look.edge} strokeOpacity="0.6" strokeWidth="0.4" />
        </g>
      )}
      <Closure c={c} uid={uid} />
    </g>
  );
}

// ------------------------------------------------------------------ reflections
/** Reflection strip: a band following the outline, faded at both ends. */
function Strip({ c, a, b, y0, y1, fade, opacity, blur }: { c: ContainerModel; a: number; b: number; y0: number; y1: number; fade: string; opacity: number; blur?: string }) {
  if (opacity <= 0.005) return null;
  return <path d={bandPath(c, a, b, y0, y1)} fill={`url(#${fade})`} opacity={opacity} filter={blur ? `url(#${blur})` : undefined} />;
}

export function ProductGloss({ c, material, uid, viewAngleDeg = 180 }: Props) {
  const H = c.heightMm;
  const look = MATERIAL_LOOK[material];
  const clear = look.opacity < 0.9;
  const pose = handlePose(c, viewAngleDeg);
  const id = (s: string) => `${uid}-${s}`;
  const y0 = c.closure && c.closure.kind !== 'reeds' ? 2.5 : 1.2;
  const y1 = H - 1.5;
  const matte = !!look.coated;
  const frost = look.frosted;
  // overall strength of specular reflections per surface
  const k = look.metal ? 0.85 : matte ? (look.base === 'dark' ? 0.75 : 0.45) : frost ? 0.4 : look.base === 'dark' ? 0.95 : look.base === 'white' ? 0.7 : 1;
  const fade = (o: number) => [
    { o: 0, c: '#ffffff', a: 0 },
    { o: (y0 + (y1 - y0) * o) / H, c: '#ffffff', a: 1 },
    { o: (y1 - (y1 - y0) * o) / H, c: '#ffffff', a: 1 },
    { o: 1, c: '#ffffff', a: 0 },
  ];
  return (
    <g pointerEvents="none">
      <defs>
        <filter id={id('soft')} x="-50%" y="-5%" width="200%" height="110%">
          <feGaussianBlur stdDeviation="0.7" />
        </filter>
        <filter id={id('softer')} x="-50%" y="-5%" width="200%" height="110%">
          <feGaussianBlur stdDeviation={matte || frost ? 3.2 : 2} />
        </filter>
        <Grad id={id('fadeA')} user={{ y1: 0, y2: H }} stops={fade(0.04)} />
        <Grad id={id('fadeB')} user={{ y1: 0, y2: H }} stops={fade(0.12)} />
        <Grad id={id('shoulder')} user={{ y1: 0, y2: H }} stops={slopeStops(c, 'light', 1, '#ffffff')} />
      </defs>
      {/* softbox on the left: broad glow + crisp core */}
      <Strip c={c} a={0.74} b={0.4} y0={y0} y1={y1} fade={id('fadeB')} opacity={(clear ? 0.35 : 0.42) * k} blur={id('softer')} />
      {!matte && <Strip c={c} a={0.64} b={0.5} y0={y0 + 1} y1={y1 - 1} fade={id('fadeA')} opacity={(clear ? 0.82 : 0.6) * k} blur={frost ? id('softer') : id('soft')} />}
      {!matte && !frost && <Strip c={c} a={0.71} b={0.685} y0={y0 + 2} y1={y1 - 2} fade={id('fadeA')} opacity={0.75 * k} />}
      {/* shoulders and curved parts catch the light from above */}
      <path d={bandPath(c, 0.85, -0.2, 0, H)} fill={`url(#${id('shoulder')})`} opacity={(clear ? 0.75 : matte ? 0.25 : 0.5) * k} filter={`url(#${id('soft')})`} />
      {/* strip light on the right and the rim light */}
      <Strip c={c} a={-0.62} b={-0.78} y0={y0 + 2} y1={y1 - 2} fade={id('fadeB')} opacity={(clear ? 0.4 : 0.2) * k} blur={id('softer')} />
      {!matte && <Strip c={c} a={-0.7} b={-0.74} y0={y0 + 3} y1={y1 - 3} fade={id('fadeA')} opacity={(clear ? 0.55 : 0.35) * k} blur={id('soft')} />}
      <Strip c={c} a={-0.9} b={-0.965} y0={y0 + 2} y1={y1 - 2} fade={id('fadeA')} opacity={(matte ? 0.2 : 0.45) * k} blur={id('soft')} />
      {/* brushed steel: fine vertical brushing */}
      {look.metal && (
        <g>
          {Array.from({ length: 26 }, (_, i) => {
            const t = -0.95 + (1.9 * i) / 25;
            return <path key={i} d={bandPath(c, t, t - 0.01, y0 + 1, y1 - 1)} fill={i % 2 ? '#fff' : '#000'} opacity={0.035 + ((i * 7) % 5) * 0.01} />;
          })}
        </g>
      )}
      {pose && pose.facing >= 0 && <Handle c={c} material={material} uid={uid} viewAngleDeg={viewAngleDeg} />}
    </g>
  );
}
